/**
 * Slot picker for the consultation dialog.
 *
 * The same HubSpot availability the homepage scheduler shows, sized for a
 * dialog: a row of days, then that day's free times. Picking one is optional —
 * the enquiry stands on its own, and if live booking is switched off, or the
 * calendar has nothing free, this renders nothing at all rather than a dead
 * control.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { apiHeaders, config } from '../../../auth/config';

interface SlotDto {
  label: string;
  startMillisUtc: number;
  endMillisUtc: number;
}

interface DayDto {
  dow: string;
  date: number;
  month: string;
  iso: string;
  slots: SlotDto[];
}

interface AvailabilityDto {
  days: DayDto[];
  range: string;
  durationMinutes: number;
  timezone: string;
}

/** What the dialog needs back: enough to book it and to name it on screen. */
export interface PickedSlot {
  startMillisUtc: number;
  endMillisUtc: number;
  /** `Thu 24 Sep · 14:00`, for the confirmation line. */
  label: string;
}

interface Props {
  value: PickedSlot | null;
  onChange: (slot: PickedSlot | null) => void;
}

export const ElfSlotPicker: React.FC<Props> = ({ value, onChange }) => {
  const [availability, setAvailability] = useState<AvailabilityDto | null>(null);
  const [dayIso, setDayIso] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'off'>('loading');

  useEffect(() => {
    let cancelled = false;
    fetch(`${config.api.baseUrl}/api/scheduler/availability`, { headers: apiHeaders() })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((body) => {
        if (cancelled) return;
        const data: AvailabilityDto | null = body?.availability ?? null;
        const days = data?.days?.filter((d) => d.slots.length) ?? [];
        if (!body?.enabled || !days.length) {
          setState('off');
          return;
        }
        setAvailability({ ...(data as AvailabilityDto), days });
        setDayIso(days[0].iso);
        setState('ready');
      })
      .catch(() => {
        // Availability is a bonus on this form: if it can't be read, the
        // enquiry still sends, so the picker simply isn't offered.
        if (!cancelled) setState('off');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const day = useMemo(
    () => availability?.days.find((d) => d.iso === dayIso) ?? availability?.days[0] ?? null,
    [availability, dayIso],
  );

  const pick = useCallback(
    (slot: SlotDto) => {
      if (value?.startMillisUtc === slot.startMillisUtc) {
        onChange(null);
        return;
      }
      const d = day;
      onChange({
        startMillisUtc: slot.startMillisUtc,
        endMillisUtc: slot.endMillisUtc,
        label: d ? `${d.dow} ${d.date} ${d.month} · ${slot.label}` : slot.label,
      });
    },
    [day, onChange, value],
  );

  if (state === 'off') return null;

  if (state === 'loading') {
    return (
      <div className="elf-slotpick elf-slotpick--loading">
        <Loader2 size={14} className="elf-edit__spin" />
        Checking the calendar…
      </div>
    );
  }

  return (
    <div className="elf-slotpick">
      <div className="elf-slotpick__head">
        <span className="colhead">Pick a time (optional)</span>
        <span className="elf-slotpick__tz">
          {availability?.durationMinutes} min · {availability?.timezone.replace(/_/g, ' ')}
        </span>
      </div>

      <div className="elf-slotpick__days">
        {availability?.days.map((d) => (
          <button
            key={d.iso}
            type="button"
            className="elf-slotpick__day"
            aria-pressed={d.iso === day?.iso}
            onClick={() => setDayIso(d.iso)}
          >
            <b>{d.dow}</b>
            {d.date} {d.month}
          </button>
        ))}
      </div>

      <div className="elf-slotpick__times">
        {day?.slots.map((s) => (
          <button
            key={s.startMillisUtc}
            type="button"
            className="elf-slotpick__time"
            aria-pressed={value?.startMillisUtc === s.startMillisUtc}
            onClick={() => pick(s)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {value ? (
        <p className="elf-slotpick__chosen">
          Holding <b>{value.label}</b> — sent when you submit.
          <button type="button" onClick={() => onChange(null)}>
            Clear
          </button>
        </p>
      ) : (
        <p className="elf-slotpick__note">
          Choose a slot to have it booked with your enquiry, or leave it and we&rsquo;ll call to
          arrange one.
        </p>
      )}
    </div>
  );
};

export default ElfSlotPicker;
